/**
 * F2-C — impersonation ("ver como alumno") controller + reactive view.
 *
 * Starting an impersonation mints a short-lived JWT server-side (cap
 * `ImpersonateUsers`), stores it as a FLAGGED `StoredAccount` overlay
 * (`impersonation`) and clears the query cache so every subsequent REST
 * call refetches AS the target (the API client attaches the token as
 * `X-Impersonation-Token`). A persistent banner then offers "salir", which drops
 * the token (client-side) and pings `DELETE /api/admin/impersonation` for audit.
 */

import { createSignal, onCleanup, onMount } from "solid-js";
import { handleApiResponse, makeImpersonationAccount, type StoredAccount } from "@space/shared";
import { api } from "@/lib/api";
import {
  IMPERSONATION_CHANGED_EVENT,
  IMPERSONATION_STORAGE_KEY,
  readActiveAccountSelector,
  readImpersonationAccountSync,
} from "@/lib/accounts";
import { queryClient } from "@/lib/query-client";

function writeOverlay(account: StoredAccount | null): void {
  try {
    if (account) {
      globalThis.localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(account));
    } else {
      globalThis.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (private mode) — impersonation simply won't persist.
  }
  try {
    globalThis.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
  } catch {
    // No DOM (SSR) — nothing to notify.
  }
}

/**
 * Starts impersonating `targetUserId`. Returns the target's display name on
 * success (for the caller's toast) or throws on failure. After this resolves the
 * query cache is cleared so views refetch under the impersonated identity.
 */
export async function startImpersonation(targetUserId: string): Promise<string> {
  const actorId = readActiveAccountSelector();
  const res = await handleApiResponse(
    api.POST("/api/admin/users/{id}/impersonate", {
      params: { path: { id: targetUserId } },
    }),
  );
  const account = makeImpersonationAccount({
    userId: res.targetUserId,
    email: res.targetEmail,
    displayName: res.targetName,
    role: res.targetRole,
    token: res.token,
    actorId: actorId ?? "",
  });
  writeOverlay(account);
  // Refetch everything under the new identity.
  queryClient.clear();
  return res.targetName;
}

/**
 * Exits the active impersonation: drops the overlay token immediately (so the
 * client stops sending the header) and best-effort pings the server for audit.
 * Clears the query cache so views refetch as the admin again.
 */
export async function stopImpersonation(): Promise<void> {
  writeOverlay(null);
  queryClient.clear();
  try {
    await api.DELETE("/api/admin/impersonation", {});
  } catch {
    // Audit-only; the token is already dropped client-side.
  }
}

/** Reactive view of the active impersonation overlay (null when not impersonating). */
export function useImpersonation() {
  const [entry, setEntry] = createSignal<StoredAccount | null>(readImpersonationAccountSync());

  onMount(() => {
    const reload = () => setEntry(readImpersonationAccountSync());
    const onStorage = (e: StorageEvent) => {
      if (e.key === IMPERSONATION_STORAGE_KEY || e.key === null) {
        reload();
      }
    };
    globalThis.addEventListener("storage", onStorage);
    globalThis.addEventListener(IMPERSONATION_CHANGED_EVENT, reload);
    onCleanup(() => {
      globalThis.removeEventListener("storage", onStorage);
      globalThis.removeEventListener(IMPERSONATION_CHANGED_EVENT, reload);
    });
  });

  return {
    entry,
    isImpersonating: () => entry() !== null,
  };
}
